/**
 * Quick Add Drawer Manager
 * Handles opening, closing, and managing product quick add drawers
 */
class QuickAddDrawerManager {
  constructor() {
    this.activeDrawers = new Set();
    this.elementCache = new Map();
    this.optionCache = new Map(); // Cache for option queries
    this.variantCache = new Map(); // Cache for parsed variants
    this.isOpening = false; // Flag to prevent closing when opening
    this.debounceTimers = new Map(); // Debounce timers for each drawer
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupKeyboardSupport();
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait, key) {
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key));
    }

    const timer = setTimeout(() => {
      func();
      this.debounceTimers.delete(key);
    }, wait);

    this.debounceTimers.set(key, timer);
  }

  bindEvents() {
    // Optimized event delegation with early returns
    document.addEventListener("click", this.handleClick.bind(this));
    document.addEventListener("change", this.handleChange.bind(this));
  }

  /**
   * Optimized click handler with early returns
   */
  handleClick(e) {
    // Quick add trigger
    if (e.target.matches("[data-quick-add-trigger]")) {
      const productId = e.target.dataset.productId;
      const sectionId = e.target.dataset.sectionId;
      this.openDrawer(productId, sectionId);
      return;
    }

    // Close drawer
    if (e.target.matches("[data-close-drawer]")) {
      const productId = e.target.dataset.productId;
      this.closeDrawer(productId);
      return;
    }

    // Form submission
    if (e.target.matches(".drawer-footer-button")) {
      this.handleFormSubmission(e);
      return;
    }

    // Click outside (only check if drawers are open)
    if (this.activeDrawers.size > 0 && !this.isOpening) {
      this.handleClickOutside(e);
    }
  }

  /**
   * Optimized change handler
   */
  handleChange(e) {
    const drawer = e.target.closest(".quick-add-drawer");
    if (drawer) {
      this.handleOptionChange(e);
    }
  }

  setupKeyboardSupport() {
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeAllDrawers();
      }
    });
  }

  /**
   * Open a specific drawer
   */
  openDrawer(productId, sectionId) {
    try {
      this.isOpening = true; // Set flag to prevent accidental close

      const drawer = this.getDrawerElement(productId);

      if (!drawer) {
        console.error(`Drawer element not found for product: ${productId}`);
        return;
      }

      // Close any other open drawers first
      this.closeAllDrawers();

      // Show drawer
      drawer.style.bottom = "0";
      drawer.setAttribute("aria-hidden", "false");

      // Disable card click overlay when drawer is open
      const cardWrapper = drawer.closest(".card-wrapper");
      if (cardWrapper) {
        const overlay = cardWrapper.querySelector(".card-click-overlay");
        if (overlay) {
          overlay.style.pointerEvents = "none";
        }
      }

      // Add to active drawers
      this.activeDrawers.add(productId);

      // Initialize drawer state
      this.initializeDrawerState(productId);

      // Focus management
      this.trapFocus(drawer);

      // Reset opening flag after a short delay
      setTimeout(() => {
        this.isOpening = false;
      }, 200);

      console.log(`Drawer opened for product: ${productId}`);
    } catch (error) {
      console.error("Error opening drawer:", error);
      this.isOpening = false; // Reset flag on error
    }
  }

  /**
   * Close a specific drawer with cleanup
   */
  closeDrawer(productId) {
    const drawer = this.getDrawerElement(productId);

    if (drawer) {
      // Hide drawer
      drawer.style.bottom = "-100%";
      drawer.setAttribute("aria-hidden", "true");

      // Enable card click overlay when drawer is closed
      const cardWrapper = drawer.closest(".card-wrapper");
      if (cardWrapper) {
        const overlay = cardWrapper.querySelector(".card-click-overlay");
        if (overlay) {
          overlay.style.pointerEvents = "auto";
        }
      }

      // Cleanup drawer resources
      this.cleanupDrawer(productId);

      // Release focus
      this.releaseFocus();

      console.log(`Drawer closed for product: ${productId}`);
    }
  }

  /**
   * Close all open drawers
   */
  closeAllDrawers() {
    this.activeDrawers.forEach((productId) => {
      this.closeDrawer(productId);
    });
  }

  /**
   * Handle click outside drawer to close
   */
  handleClickOutside(event) {
    // Don't close if we're currently opening a drawer
    if (this.isOpening) return;

    // Check if click is outside any active drawer
    const clickedInsideDrawer = event.target.closest(".quick-add-drawer");
    const clickedOnTrigger = event.target.matches("[data-quick-add-trigger]");

    // If clicked outside drawer and not on trigger button, close all drawers
    if (!clickedInsideDrawer && !clickedOnTrigger) {
      this.closeAllDrawers();
    }
  }

  /**
   * Handle form submission and loading state
   */
  handleFormSubmission(event) {
    const button = event.target;
    const form = button.closest("form");

    if (form) {
      // Add loading class to button
      button.classList.add("loading");

      // Remove loading class after form submission (success or error)
      form.addEventListener("submit", () => {
        // Reset loading state after a delay
        setTimeout(() => {
          button.classList.remove("loading");
        }, 2000); // Adjust timing as needed
      });
    }
  }

  /**
   * Get drawer element with caching
   */
  getDrawerElement(productId) {
    if (!this.elementCache.has(`drawer-${productId}`)) {
      const element = document.getElementById(`quick-add-drawer-${productId}`);
      this.elementCache.set(`drawer-${productId}`, element);
    }
    return this.elementCache.get(`drawer-${productId}`);
  }

  /**
   * Handle option changes in drawer with debouncing
   */
  handleOptionChange(event) {
    const container = event.target.closest(".quick-add-drawer");
    if (!container) return;

    const productId = container.getAttribute("data-product-id");

    // Debounce variant updates to prevent excessive calls
    this.debounce(
      () => {
        this.updateVariantSelection(productId);
      },
      100,
      `variant-${productId}`
    );
  }

  /**
   * Initialize drawer state
   */
  initializeDrawerState(productId) {
    const container = this.getDrawerElement(productId);
    if (!container) return;

    // Initialize variant selection
    this.updateVariantSelection(productId);

    // Initialize option descriptions
    this.initializeOptionDescriptions(productId);
  }

  /**
   * Update variant selection based on current options
   */
  updateVariantSelection(productId) {
    const container = this.getDrawerElement(productId);
    if (!container) return;

    try {
      const variants = this.getVariants(container);
      const selectedOptions = this.getSelectedOptions(container);
      const variant = this.findVariant(variants, selectedOptions);

      this.updateAddToCartButton(container, variant);
      this.updateSizeDescription(container, selectedOptions);
    } catch (error) {
      console.error("Error updating variant selection:", error);
    }
  }

  /**
   * Get variants from container with caching
   */
  getVariants(container) {
    const cacheKey = container.getAttribute("data-variants");

    if (this.variantCache.has(cacheKey)) {
      return this.variantCache.get(cacheKey);
    }

    try {
      const variants = cacheKey ? JSON.parse(cacheKey) : [];
      this.variantCache.set(cacheKey, variants);
      return variants;
    } catch (e) {
      console.error("Error parsing variants:", e);
      return [];
    }
  }

  /**
   * Get selected options from container with caching
   */
  getSelectedOptions(container) {
    const containerId = container.id;

    // Check cache first
    if (this.optionCache.has(containerId)) {
      const cached = this.optionCache.get(containerId);
      // Verify cache is still valid by checking if elements exist
      if (cached.elements.every((el) => el.isConnected)) {
        return this.getCurrentValues(cached.elements);
      }
    }

    // Cache miss or invalid cache - rebuild
    const optionWrappers = container.querySelectorAll("[data-option-index]");
    const elements = Array.from(optionWrappers).map((wrapper) => ({
      wrapper,
      index: parseInt(wrapper.getAttribute("data-option-index")),
      radio: wrapper.querySelector(
        'input[type="radio"][data-option-value-select]:checked'
      ),
      select: wrapper.querySelector("select[data-option-dropdown]"),
    }));

    // Cache the elements
    this.optionCache.set(containerId, { elements });

    return this.getCurrentValues(elements);
  }

  /**
   * Get current values from cached elements
   */
  getCurrentValues(elements) {
    const selected = [];

    elements.forEach(({ index, radio, select }) => {
      let value = "";
      if (radio) {
        value = radio.value;
      } else if (select) {
        value = select.value;
      }
      selected[index] = value;
    });

    return selected;
  }

  /**
   * Find matching variant
   */
  findVariant(variants, options) {
    return (
      variants.find((variant) => {
        if (!variant) return false;
        return variant.options.every(
          (option, index) => option === options[index]
        );
      }) || null
    );
  }

  /**
   * Update add to cart button state with optimized style updates
   */
  updateAddToCartButton(container, variant) {
    const button = container.querySelector(".drawer-footer-button");
    const variantInput = container.querySelector('[name="id"]');

    if (!button) return;

    // Update variant ID
    if (variant && variantInput) {
      variantInput.value = variant.id;
    }

    // Find text span (preserve close button)
    const textSpan = button.querySelector("span:not([onclick])");

    // Remove loading state when updating button
    button.classList.remove("loading");

    // Batch style updates for better performance
    let buttonState;
    if (!variant) {
      if (textSpan) textSpan.textContent = "Unavailable";
      buttonState = { disabled: true, opacity: "0.6", cursor: "not-allowed" };
    } else if (variant.available) {
      if (textSpan) textSpan.textContent = "ADD TO CART";
      buttonState = { disabled: false, opacity: "1", cursor: "pointer" };
    } else {
      if (textSpan) textSpan.textContent = "SOLD OUT";
      buttonState = { disabled: true, opacity: "0.6", cursor: "not-allowed" };
    }

    // Apply all style changes at once
    button.disabled = buttonState.disabled;
    button.style.cssText = `opacity: ${buttonState.opacity}; cursor: ${buttonState.cursor};`;
  }

  /**
   * Update size description
   */
  updateSizeDescription(container, selectedOptions) {
    const sizeDesc = container.querySelector('[id^="size-description-"]');
    if (!sizeDesc) return;

    const size = selectedOptions[0];
    if (size && (size.includes("5") || size.includes("LBS"))) {
      sizeDesc.style.display = "block";
    } else {
      sizeDesc.style.display = "none";
    }
  }

  /**
   * Initialize option descriptions
   */
  initializeOptionDescriptions(productId) {
    const container = this.getDrawerElement(productId);
    if (!container) return;

    const descriptions = container.querySelectorAll(
      '[id$="-description-' + productId + '"]'
    );

    descriptions.forEach((descElement) => {
      const optionName = descElement.id.split("-")[0];

      // Set descriptions based on option type
      const descriptions = {
        size: "Choose your preferred size",
        color: "Select your color preference",
        material: "Choose your material",
        grind: "Select your grind preference",
        style: "Choose your style",
      };

      if (descriptions[optionName]) {
        descElement.innerHTML = descriptions[optionName];
      }
    });
  }

  /**
   * Trap focus within drawer
   */
  trapFocus(drawer) {
    const focusableElements = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * Release focus trap
   */
  releaseFocus() {
    // Return focus to trigger element or body
    document.body.focus();
  }

  /**
   * Cleanup method for memory management
   */
  cleanup() {
    // Clear all caches
    this.elementCache.clear();
    this.optionCache.clear();
    this.variantCache.clear();

    // Clear active drawers
    this.activeDrawers.clear();

    // Clear all debounce timers
    this.debounceTimers.forEach((timer) => clearTimeout(timer));
    this.debounceTimers.clear();

    // Reset flags
    this.isOpening = false;
  }

  /**
   * Cleanup specific drawer
   */
  cleanupDrawer(productId) {
    // Remove from active drawers
    this.activeDrawers.delete(productId);

    // Clear specific caches
    this.elementCache.delete(`drawer-${productId}`);
    this.optionCache.delete(`quick-add-drawer-${productId}`);

    // Clear debounce timer for this drawer
    const timerKey = `variant-${productId}`;
    if (this.debounceTimers.has(timerKey)) {
      clearTimeout(this.debounceTimers.get(timerKey));
      this.debounceTimers.delete(timerKey);
    }
  }
}

// Initialize the drawer manager
const drawerManager = new QuickAddDrawerManager();

// Global functions for backward compatibility
window.openQuickAddDrawer = function (productId, sectionId) {
  drawerManager.openDrawer(productId, sectionId);
};

window.closeQuickAddDrawer = function (productId) {
  drawerManager.closeDrawer(productId);
};

window.initializeQuickAddDrawer = function (productId) {
  drawerManager.initializeDrawerState(productId);
};

// Global cleanup function
window.cleanupQuickAddDrawer = function () {
  drawerManager.cleanup();
};

// Export for module usage if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = QuickAddDrawerManager;
}
